import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ActionButton } from '@/components/ui/ActionButton';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/lib/api';
import type { ApprovalRequest } from '@/lib/types';
import { formatDate } from '../format';
import { deriveIdempotencyKey } from '../idempotency';
import { BucketShell } from './_BucketShell';
import { readStructuredFields } from './_payload';

const SLUG = 'ap-purchase-invoice';
const QK_QUEUE = ['queue', SLUG];
const QK_HISTORY = ['history', SLUG];
const QK_STATS = ['stats', SLUG];

export interface PostFailedViewProps {
  req: ApprovalRequest;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

function errorDetail(err: unknown, fallback: string): string {
  const raw = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object') return JSON.stringify(raw);
  return (err as { message?: string })?.message ?? fallback;
}

export function PostFailedView({ req }: PostFailedViewProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [repairReason, setRepairReason] = useState('');
  const sf = readStructuredFields(req);
  const errorMessage = readString(sf['error_message']);
  const failedNode = readString(sf['failed_node']);
  const failedAt = readString(sf['failed_at']);
  const oracleErrorCode = readString(sf['oracle_error_code']);
  const preflight = asRecord(sf['service_preflight']);
  const hasServicePreflight = Object.keys(preflight).length > 0;
  const isAdmin = Boolean(user?.is_admin);

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: QK_QUEUE });
    void queryClient.invalidateQueries({ queryKey: QK_HISTORY });
    void queryClient.invalidateQueries({ queryKey: QK_STATS });
    void queryClient.invalidateQueries({ queryKey: ['detail', SLUG, req.id] });
  };

  const retryMutation = useMutation({
    mutationFn: async () => {
      const idem = deriveIdempotencyKey(req.id, req.updated_at, 'post-service-final');
      const res = await apiClient.post(
        `/automations/${SLUG}/${req.id}/post-service-final`,
        {
          updated_at: req.updated_at,
          preflight,
        },
        { headers: { 'Idempotency-Key': idem } },
      );
      return res.data as { lifecycle?: string; request?: ApprovalRequest };
    },
    onSuccess: (data) => {
      if (data.request) {
        queryClient.setQueryData(['detail', SLUG, data.request.id], data.request);
      }
      toast({
        variant: 'success',
        title: 'Service post retried',
        description:
          data.lifecycle === 'posted'
            ? 'Oracle readback or retry confirmed the service invoice.'
            : 'The retry completed; refreshing the queue.',
      });
      invalidateAll();
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Retry failed',
        description: errorDetail(err, 'Service post retry failed'),
      });
    },
  });

  const repairMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(
        `/automations/${SLUG}/${req.id}/admin/repair-post-failed`,
        { reason: repairReason.trim() },
      );
      return res.data as { request?: ApprovalRequest };
    },
    onSuccess: (data) => {
      if (data.request) {
        queryClient.setQueryData(['detail', SLUG, data.request.id], data.request);
      }
      setRepairReason('');
      toast({
        variant: 'success',
        title: 'Post failure repaired',
        description: 'Oracle readback confirmed the invoice and the row was repaired.',
      });
      invalidateAll();
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Repair blocked',
        description: errorDetail(err, 'Oracle readback could not safely repair this row'),
      });
    },
  });

  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-error-500/40 bg-error-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-error-700">
          Post failed
        </p>
        <p className="mt-8 font-mono text-sm text-error-700">{errorMessage ?? '—'}</p>
        <dl className="mt-12 grid gap-12 text-sm tablet:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Failed node
            </dt>
            <dd className="mt-2 font-mono text-neutral-900">{failedNode ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Failed at
            </dt>
            <dd className="mt-2 text-neutral-900">{formatDate(failedAt)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Oracle code
            </dt>
            <dd className="mt-2 font-mono text-neutral-900">{oracleErrorCode ?? '—'}</dd>
          </div>
        </dl>
        <p className="mt-12 text-xs text-neutral-600">
          Retry posting after checking the error details; the same request will be
          resubmitted to Oracle with an idempotent audit trail.
        </p>
        <div className="mt-12 flex flex-wrap items-center gap-8">
          {hasServicePreflight && (
            <ActionButton
              variant="secondary"
              onClick={() => retryMutation.mutate()}
              loading={retryMutation.isPending}
            >
              Retry service post
            </ActionButton>
          )}
        </div>
      </section>

      {isAdmin && (
        <section className="rounded-lg border border-neutral-200 bg-neutral-0 p-16">
          <label
            htmlFor={`repair-reason-${req.id}`}
            className="text-xs font-semibold uppercase tracking-wide text-neutral-500"
          >
            Repair reason
          </label>
          <textarea
            id={`repair-reason-${req.id}`}
            className="mt-8 block min-h-[96px] w-full resize-y rounded-md border border-neutral-200 bg-neutral-0 px-12 py-8 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            value={repairReason}
            onChange={(e) => setRepairReason(e.target.value)}
            placeholder="Why is this POST_FAILED row safe to repair from Oracle readback?"
          />
          <div className="mt-12 flex justify-end">
            <ActionButton
              variant="secondary"
              onClick={() => repairMutation.mutate()}
              disabled={!repairReason.trim() || repairMutation.isPending}
              loading={repairMutation.isPending}
            >
              Repair posted row
            </ActionButton>
          </div>
          <p className="mt-8 text-xs text-neutral-500">
            Admin repair only performs the backend Oracle readback path; unsafe
            matches remain blocked.
          </p>
        </section>
      )}
    </BucketShell>
  );
}
